/**
 * The color models behind the custom color picker (KPR-314): hex, RGB and
 * HSB, converted both ways, and the parsers for what a person types into
 * each field.
 *
 * HSB (a.k.a. HSV) is the picker's own model: h in 0..360, s and b in 0..100.
 * Values stay unrounded floats while they move through the picker and are
 * rounded only where they are shown or stored, so an HSB the person typed is
 * not nudged by a trip through 8-bit RGB.
 *
 * The stored spelling is the one elementFormat/color.js defines (a lower-case
 * "#rrggbb"); this module only ever produces that spelling.
 */

export const clamp = (value, low, high) => Math.min(high, Math.max(low, value));

/** Where a picker with no color to start from begins: the palette's blue. */
export const DEFAULT_HSB = Object.freeze({ h: 219, s: 80, b: 93 });

const channelHex = n => clamp(Math.round(n), 0, 255).toString(16).padStart(2, '0');

/** {r,g,b} (0..255) -> "#rrggbb", lower case. */
export const rgbToHex = ({ r, g, b }) => `#${channelHex(r)}${channelHex(g)}${channelHex(b)}`;

/** {h,s,b} -> {r,g,b}, whole numbers 0..255. h wraps (360 is 0); s and b clamp. */
export function hsbToRgb({ h, s, b }) {
	const hue = ((((Number(h) || 0) % 360) + 360) % 360);
	const sat = clamp(Number(s) || 0, 0, 100) / 100;
	const val = clamp(Number(b) || 0, 0, 100) / 100;
	const chroma = val * sat;
	const x = chroma * (1 - Math.abs(((hue / 60) % 2) - 1));
	const m = val - chroma;
	const [r1, g1, b1] = [
		[chroma, x, 0], [x, chroma, 0], [0, chroma, x], [0, x, chroma], [x, 0, chroma], [chroma, 0, x],
	][Math.floor(hue / 60) % 6];
	return {
		r: Math.round((r1 + m) * 255),
		g: Math.round((g1 + m) * 255),
		b: Math.round((b1 + m) * 255),
	};
}

/** {r,g,b} -> {h,s,b} as floats (h in 0..360, exclusive). A gray has hue 0. */
export function rgbToHsb({ r, g, b }) {
	const [rn, gn, bn] = [r, g, b].map(v => clamp(v, 0, 255) / 255);
	const max = Math.max(rn, gn, bn);
	const min = Math.min(rn, gn, bn);
	const delta = max - min;
	let h = 0;
	if (delta > 0) {
		if (max === rn) h = ((gn - bn) / delta) % 6;
		else if (max === gn) h = ((bn - rn) / delta) + 2;
		else h = ((rn - gn) / delta) + 4;
		h = (h * 60 + 360) % 360;
	}
	return { h, s: max === 0 ? 0 : (delta / max) * 100, b: max * 100 };
}

export const hsbToHex = hsb => rgbToHex(hsbToRgb(hsb));

/**
 * The HSB for an RGB the person just entered, keeping what RGB cannot say.
 * A gray has no hue and black has no saturation either; taking them as 0
 * would snap the sliders to red the moment a channel is typed. So the
 * previous hue (and, for black, saturation) is kept.
 */
export function hsbFromRgb(rgb, previous = DEFAULT_HSB) {
	const next = rgbToHsb(rgb);
	if (next.b === 0) return { h: previous.h, s: previous.s, b: 0 };
	if (next.s === 0) return { ...next, h: previous.h };
	return next;
}

// 3 or 6 digits, one optional "#", nothing else. Not 4 or 8: those carry
// alpha, which keepr does not store (elementFormat/color.js). A "#" is never
// required, because a person pasting "2F6FED" from a design tool means it.
const HEX_INPUT = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i;

/** What a person typed in the Hex field -> {r,g,b}, or null when it is not a 3- or 6-digit hex. */
export function parseHexInput(text) {
	const match = HEX_INPUT.exec(`${text ?? ''}`.trim());
	if (!match) return null;
	const digits = match[1].length === 3 ? match[1].split('').map(c => c + c).join('') : match[1];
	return { r: parseInt(digits.slice(0, 2), 16), g: parseInt(digits.slice(2, 4), 16), b: parseInt(digits.slice(4, 6), 16) };
}

/**
 * What a person typed in a numeric field -> a whole number 0..max, or null.
 * Whole numbers only: no sign, no decimal point, no exponent ("1e2" is not
 * 100), at most three digits so a pasted number cannot be megabytes long.
 */
export function parseChannelInput(text, max) {
	const trimmed = `${text ?? ''}`.trim();
	if (!/^\d{1,3}$/.test(trimmed)) return null;
	const value = Number(trimmed);
	return value <= max ? value : null;
}
